import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import client, { apiError } from "@/lib/api";

const PENDING_KEY = "criteri_pending_analysis";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const hasPending = typeof window !== "undefined" && !!sessionStorage.getItem(PENDING_KEY);
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [accepted, setAccepted] = useState(false);
    const [marketing, setMarketing] = useState(false);
    const [errors, setErrors] = useState({});
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const validate = () => {
        const errs = {};
        if (form.name.trim().length < 2) errs.name = "Dinos cómo te llamas.";
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = "Escribe un correo válido.";
        if (form.password.length < 8 || !/[A-Za-z]/.test(form.password) || !/\d/.test(form.password))
            errs.password = "Al menos 8 caracteres, con letras y números.";
        if (!accepted) errs.accepted = "Debes aceptar la política de privacidad y las condiciones para continuar.";
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!validate()) return;
        setLoading(true);
        try {
            await register(form.name.trim(), form.email.trim(), form.password);
            // Si viene del test anónimo, asociamos ese análisis al nuevo usuario y vamos al neceser.
            const raw = sessionStorage.getItem(PENDING_KEY);
            if (raw) {
                try { await client.post("/analyses", JSON.parse(raw)); } catch (err) { /* el análisis se podrá rehacer si fallara */ }
                sessionStorage.removeItem(PENDING_KEY);
                navigate("/app/neceser", { replace: true });
            } else {
                navigate("/app/analisis/nuevo", { replace: true });
            }
        } catch (err) {
            setError(apiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthShell
            title={hasPending ? "Guarda tu análisis y continúa" : "Empieza a cuidarte con cabeza"}
            subtitle={hasPending
                ? "Crea tu espacio privado para guardar lo que ya hemos entendido de ti y continuar con tu neceser."
                : "Crea tu cuenta gratis. En unos minutos sabrás qué necesita tu piel y qué te sobra del neceser."}
            footer={
                <Link to="/acceder" className="font-medium text-terracotta hover:underline" data-testid="register-have-account">
                    Ya tengo cuenta
                </Link>
            }
        >
            <form onSubmit={submit} className="space-y-5" noValidate>
                {error && (
                    <div data-testid="register-error" className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive">
                        {error}
                    </div>
                )}
                <div className="space-y-2">
                    <Label htmlFor="name">Tu nombre</Label>
                    <Input id="name" value={form.name} onChange={set("name")} data-testid="register-name"
                        placeholder="Lucía" onBlur={validate} />
                    {errors.name && <p className="text-[13px] text-destructive" data-testid="register-name-error">{errors.name}</p>}
                </div>
                <div className="space-y-2">
                    <Label htmlFor="email">Correo electrónico</Label>
                    <Input id="email" type="email" value={form.email} onChange={set("email")}
                        data-testid="register-email" placeholder="tu@correo.es" onBlur={validate} />
                    {errors.email && <p className="text-[13px] text-destructive" data-testid="register-email-error">{errors.email}</p>}
                </div>
                <div className="space-y-2">
                    <Label htmlFor="password">Contraseña</Label>
                    <Input id="password" type="password" value={form.password} onChange={set("password")}
                        data-testid="register-password" placeholder="Mínimo 8 caracteres" onBlur={validate} />
                    {errors.password && <p className="text-[13px] text-destructive" data-testid="register-password-error">{errors.password}</p>}
                </div>
                <div className="space-y-3 pt-1">
                    <label className="flex items-start gap-2.5 text-[13px] leading-[1.5] text-subtle">
                        <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
                            data-testid="register-accept" className="mt-0.5 h-4 w-4 shrink-0 accent-terracotta" />
                        <span>
                            He leído y acepto la{" "}
                            <Link to="/privacidad" className="underline hover:text-ink">política de privacidad</Link>{" "}y el{" "}
                            <Link to="/legal" className="underline hover:text-ink">aviso legal y las condiciones</Link>.
                        </span>
                    </label>
                    {errors.accepted && <p className="text-[13px] text-destructive" data-testid="register-accept-error">{errors.accepted}</p>}
                    <label className="flex items-start gap-2.5 text-[13px] leading-[1.5] text-subtle">
                        <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)}
                            data-testid="register-marketing" className="mt-0.5 h-4 w-4 shrink-0 accent-terracotta" />
                        <span>Quiero recibir consejos y novedades por correo (opcional).</span>
                    </label>
                </div>
                <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full h-11 text-[15px]">
                    {loading ? (hasPending ? "Creando tu espacio…" : "Creando tu cuenta…") : (hasPending ? "Crear mi espacio y continuar" : "Crear mi cuenta gratis")}
                </Button>
            </form>
        </AuthShell>
    );
}
